/**
 * Multi-leg booking status helpers (partial cancel/refund + active legs).
 */

const CANCELLED_STATUSES = ['Cancelled', 'Cancelled (Refunded)'];

export function isCancelledLegStatus(status) {
  const value = String(status || '');
  if (CANCELLED_STATUSES.includes(value)) return true;
  return value.toLowerCase().includes('cancelled');
}

export function isManageableLegStatus(status) {
  return status === 'Upcoming' || status === 'Confirmed';
}

export function getBookingLegs(booking) {
  if (!booking) return [];
  if (booking.flights?.length > 0) return booking.flights;
  return [booking];
}

export function getManageableLegs(booking) {
  return getBookingLegs(booking).filter(
    (leg) =>
      isManageableLegStatus(leg.booking_status) &&
      leg.can_change_or_cancel !== false
  );
}

export function hasManageableLegs(booking) {
  return getManageableLegs(booking).length > 0;
}

export function hasMixedActiveAndCancelledLegs(booking) {
  const legs = getBookingLegs(booking);
  const hasActive = legs.some((leg) => isManageableLegStatus(leg.booking_status));
  const hasCancelled = legs.some((leg) => isCancelledLegStatus(leg.booking_status));
  return hasActive && hasCancelled;
}

/** Group/list badge status when legs differ */
export function deriveGroupBookingStatus(legs, fallbackStatus = '') {
  if (!legs?.length) return fallbackStatus;

  if (legs.some((leg) => leg.booking_status === 'RejectedByPJO')) {
    return 'RejectedByPJO';
  }
  if (legs.some((leg) => leg.booking_status === 'PendingPJOConfirmation')) {
    return 'PendingPJOConfirmation';
  }

  const activeCount = legs.filter((leg) => isManageableLegStatus(leg.booking_status)).length;
  const cancelledCount = legs.filter((leg) => isCancelledLegStatus(leg.booking_status)).length;

  if (activeCount > 0 && cancelledCount > 0) return 'PartiallyActive';
  if (activeCount > 0) return 'Upcoming';
  if (cancelledCount === legs.length) {
    return legs.find((leg) => isCancelledLegStatus(leg.booking_status))?.booking_status || fallbackStatus;
  }

  return fallbackStatus;
}

export function canShowManageFlightActions(booking) {
  if (!booking || !hasManageableLegs(booking)) return false;

  if (
    booking.requires_pjo_rejection_choice ||
    booking.has_rejected_by_pjo_legs ||
    booking.booking_status === 'RejectedByPJO'
  ) {
    return false;
  }

  if (booking.can_change_or_cancel === false) return false;

  return true;
}

/** Next departure among legs that can still be changed/cancelled */
export function getNextManageableDepartureIso(booking) {
  const legs = getManageableLegs(booking);
  const source = legs.length > 0 ? legs : getBookingLegs(booking);
  const times = source
    .map((leg) => new Date(leg.departure?.datetime || leg.departure_date_time).getTime())
    .filter((t) => Number.isFinite(t));

  if (!times.length) {
    const fallback = booking.departure?.datetime;
    return fallback || null;
  }

  return new Date(Math.min(...times)).toISOString();
}

export function getPrimaryActionBookingToken(booking) {
  const manageable = getManageableLegs(booking);
  if (manageable.length > 0) {
    return manageable[0].booking_token;
  }
  return booking.booking_token;
}

/**
 * Token for POST /bookings/cancel/:token — use group reference for multi-leg trips
 * so we do not hit a single (possibly already-cancelled) leg token.
 */
export function getCancellationApiToken(booking) {
  if (!booking) return null;

  const legs = getBookingLegs(booking);
  const isGroupTrip =
    legs.length > 1 || hasMixedActiveAndCancelledLegs(booking);

  if (isGroupTrip && booking.reference_number) {
    return booking.reference_number;
  }

  return (
    booking.booking_token ||
    getPrimaryActionBookingToken(booking) ||
    booking.reference_number
  );
}

/** Legs that will be charged / cancelled (excludes already-cancelled legs) */
export function getLegsEligibleForCancellation(booking) {
  return getManageableLegs(booking);
}

/** Matches backend resolveBookingPassengerCount — fee is per flight × passengers. */
export function resolveLegPassengerCount(leg, booking) {
  const fromLeg = parseInt(String(leg?.passenger_count ?? ''), 10);
  if (Number.isFinite(fromLeg) && fromLeg > 0) return fromLeg;

  const fromSeats = parseInt(String(leg?.selected_seat ?? ''), 10);
  if (Number.isFinite(fromSeats) && fromSeats > 0) return fromSeats;

  const fromBooking = parseInt(String(booking?.passenger_count ?? ''), 10);
  if (Number.isFinite(fromBooking) && fromBooking > 0) return fromBooking;

  return 1;
}

/**
 * Cancellation fee for remaining legs only (not already-cancelled).
 * Per leg: cancellation_fee_per_flight × passengers on that leg (same as backend).
 */
export function getApplicableCancellationFee(booking) {
  const legs = getLegsEligibleForCancellation(booking);
  const perFlight = parseFloat(booking?.cancellation_fee_per_flight);
  const defaultPerFlight = 250;

  if (legs.length > 0) {
    const rate =
      Number.isFinite(perFlight) && perFlight > 0 ? perFlight : defaultPerFlight;
    const total = legs.reduce((sum, leg) => {
      const passengers = resolveLegPassengerCount(leg, booking);
      return sum + rate * passengers;
    }, 0);
    return Math.round(total * 100) / 100;
  }

  const totalFee = parseFloat(booking?.cancellation_fee);
  if (Number.isFinite(totalFee) && totalFee > 0) {
    return totalFee;
  }

  const passengers = resolveLegPassengerCount(booking, booking);
  return defaultPerFlight * passengers;
}

export function getCancellationSummaryText(booking) {
  const legs = getLegsEligibleForCancellation(booking);
  const allLegs = getBookingLegs(booking);
  const cancelledCount = allLegs.filter((leg) =>
    isCancelledLegStatus(leg.booking_status)
  ).length;

  if (legs.length === 0) {
    return 'No flights are available to cancel.';
  }

  if (cancelledCount > 0 && legs.length < allLegs.length) {
    const legLabel = legs.length === 1 ? 'flight' : 'flights';
    return `You are about to cancel ${legs.length} remaining ${legLabel}. Already cancelled legs are not charged again.`;
  }

  return 'You are about to cancel this flight.';
}
